import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Crown, Lock, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { subscribe as subscribeApi } from "@/lib/api";

const GREEN = "#00FF87";
const TEAL = "#2DD4BF";

const PRO_FEATURES = [
  "Chat CFO illimitata",
  "Fisco + INPS automatici",
  "Investimenti + Coach + Segnali",
  "Report mensile",
  "Alert push in tempo reale",
];

const DEFAULT_PRICES = {
  monthly: { amount: 6.99, period: "mese", label: "Mensile" },
  annual: { amount: 59.0, period: "anno", label: "Annuale", monthly_equiv: 4.92, discount_pct: 30 },
};

const fmtPrice = (n) =>
  `€${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PaywallSheet = ({ open, onClose, subscription, onSubscribed }) => {
  const [plan, setPlan] = useState("annual");
  const [busy, setBusy] = useState(false);

  const prices = subscription?.prices || DEFAULT_PRICES;
  const expired = subscription && !subscription.is_premium;
  const inTrial = subscription?.in_trial;
  const daysLeft = subscription?.days_left ?? 0;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSubscribe = async () => {
    setBusy(true);
    try {
      const res = await subscribeApi(plan);
      toast.success("Liqo Pro attivo — bentornato al pieno controllo");
      onSubscribed?.(res);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attivazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const headline = expired
    ? "Prova terminata"
    : inTrial
    ? `Prova attiva · ${daysLeft} ${daysLeft === 1 ? "giorno" : "giorni"} rimasti`
    : "Sblocca tutto Liqo";

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="paywall-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/75 backdrop-blur-[3px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-10 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em]" style={{ color: GREEN }}>
                <Crown size={15} /> LIQO PRO <span className="text-white/25">//</span> SBLOCCA
              </span>
              <button
                onClick={onClose}
                data-testid="paywall-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-5">
              {/* hero */}
              <div
                className="rounded-[14px] border bg-[#0D0D14] p-5 text-center"
                style={{ borderColor: `${GREEN}2E`, boxShadow: `0 0 40px ${GREEN}12` }}
              >
                <span
                  className="mx-auto flex h-12 w-12 items-center justify-center rounded-[12px] border"
                  style={{ borderColor: `${GREEN}55`, background: `${GREEN}10`, color: GREEN }}
                >
                  {expired ? <Lock size={20} /> : <Zap size={20} />}
                </span>
                <p className="mono mt-3 text-[11px] uppercase tracking-[0.18em] text-white/40" data-testid="paywall-headline">
                  {headline}
                </p>
                <h2 className="mt-1 text-[22px] font-light leading-tight tracking-[-0.5px] text-white">
                  Il tuo CFO AI, <span style={{ color: GREEN }}>senza limiti</span>
                </h2>
                <p className="mx-auto mt-2 max-w-[300px] text-[13px] font-light leading-[1.55] text-white/50">
                  Insight proattivi, tasse sempre sotto controllo e un assistente che ti parla prima tu.
                </p>
              </div>

              {/* feature list */}
              <div className="mt-5 space-y-2.5" data-testid="paywall-features">
                {PRO_FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border"
                      style={{ borderColor: `${TEAL}44`, color: TEAL, background: `${TEAL}0F` }}
                    >
                      <Check size={13} />
                    </span>
                    <span className="text-[13.5px] font-light text-white/80">{f}</span>
                  </div>
                ))}
              </div>

              {/* selettore piano */}
              <div className="mt-6 grid grid-cols-2 gap-2.5" data-testid="paywall-plans">
                {/* Annuale */}
                <button
                  onClick={() => setPlan("annual")}
                  data-testid="paywall-plan-annual"
                  aria-pressed={plan === "annual"}
                  className="relative rounded-[12px] border p-3.5 text-left transition-colors duration-150"
                  style={
                    plan === "annual"
                      ? { borderColor: `${GREEN}77`, background: `${GREEN}0E`, boxShadow: `0 0 0 1px ${GREEN}33` }
                      : { borderColor: "rgba(255,255,255,0.1)", background: "#0D0D14" }
                  }
                >
                  {prices.annual?.discount_pct ? (
                    <span
                      className="mono absolute -top-2 right-3 rounded-[4px] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.1em]"
                      style={{ background: GREEN, color: "#031b10" }}
                    >
                      −{prices.annual.discount_pct}%
                    </span>
                  ) : null}
                  <p className="caps-tag text-white/45">{prices.annual?.label || "Annuale"}</p>
                  <p className="mono mt-1 text-[22px] font-light leading-none tabular text-white">
                    {fmtPrice(prices.annual?.amount ?? 59)}
                    <span className="text-[12px] text-white/40">/anno</span>
                  </p>
                  {prices.annual?.monthly_equiv ? (
                    <p className="mono mt-1 text-[11px] text-white/40">≈ {fmtPrice(prices.annual.monthly_equiv)}/mese</p>
                  ) : null}
                </button>

                {/* Mensile */}
                <button
                  onClick={() => setPlan("monthly")}
                  data-testid="paywall-plan-monthly"
                  aria-pressed={plan === "monthly"}
                  className="rounded-[12px] border p-3.5 text-left transition-colors duration-150"
                  style={
                    plan === "monthly"
                      ? { borderColor: `${GREEN}77`, background: `${GREEN}0E`, boxShadow: `0 0 0 1px ${GREEN}33` }
                      : { borderColor: "rgba(255,255,255,0.1)", background: "#0D0D14" }
                  }
                >
                  <p className="caps-tag text-white/45">{prices.monthly?.label || "Mensile"}</p>
                  <p className="mono mt-1 text-[22px] font-light leading-none tabular text-white">
                    {fmtPrice(prices.monthly?.amount ?? 6.99)}
                    <span className="text-[12px] text-white/40">/mese</span>
                  </p>
                  <p className="mono mt-1 text-[11px] text-white/40">flessibile</p>
                </button>
              </div>

              {/* CTA */}
              <button
                onClick={handleSubscribe}
                disabled={busy}
                data-testid="paywall-subscribe-button"
                className="mono mt-5 flex h-13 w-full items-center justify-center gap-2 rounded-[8px] border py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] active:scale-[0.98] disabled:opacity-50"
                style={{ borderColor: `${GREEN}66`, color: GREEN, background: `${GREEN}0F`, boxShadow: `0 0 28px ${GREEN}1F` }}
              >
                <Crown size={15} />
                {busy ? "Attivo…" : "Abbonati a Liqo Pro"}
              </button>

              <p className="mono mt-3 text-center text-[10px] leading-4 tracking-[0.04em] text-white/30" data-testid="paywall-disclaimer">
                Pagamento simulato (MOCK) in anteprima · su App Store via abbonamento in-app. Annulla quando vuoi.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
